import type {
  Breakpoint,
  GotedoStreamsLocalState,
  LivestreamingRoomData,
  LivestreamingSessionData,
  ResponsiveImageData,
} from '@/types';
import { log } from '@livekit/components-core';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import type { Socket } from 'socket.io-client';
import VideoIcon from '../components/VideoIcon';
import VideoOffIcon from '../components/VideoOffIcon';
import MicIcon from '../components/MicIcon';
import MicOffIcon from '../components/MicOffIcon';
import ShareScreenIcon from '../components/ScreenShareIcon';
import { Track } from 'livekit-client';
import ScreenShareStopIcon from '../components/ScreenShareStopIcon';
import React from 'react';

export const formatStreamDateTime = (
  date: Date,
  startTime: string,
  endTime: string,
  timezone: string,
) => {
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  };
  const formattedDate = date.toLocaleDateString('en-US', options);
  return `${formattedDate} • ${startTime}–${endTime} ${timezone}`;
};

export const getInitials = (name: string): string => {
  if (!name) return '';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const getFormattedGMTOffset = (date: Date): string => {
  const offsetMinutes = date.getTimezoneOffset();
  const offsetHours = -offsetMinutes / 60;
  const sign = offsetHours >= 0 ? '+' : '-';
  return `GMT${sign}${Math.abs(offsetHours)}`;
};

export const getJoinButtonState = (
  roomData: LivestreamingRoomData | null,
): { label: string; disabled: boolean; note?: string } => {
  if (!roomData) {
    return {
      label: 'Loading...',
      disabled: true,
    };
  }

  const now = new Date();
  const earliestJoin = new Date(roomData.earliest_join_time);
  // const startTime = new Date(roomData.start_time);
  const endTime = new Date(roomData.end_time);

  const isAdmin = roomData.can_commence_stream;
  const isCommenceable = roomData.is_streaming_commenceable;
  const hasStreamCommenced = roomData.has_stream_commenced;

  // Stream ended
  if (now > endTime) {
    if (roomData.has_stream_commenced && !roomData.has_stream_ended) {
      return {
        label: 'Join Stream',
        disabled: false,
        note: '',
      };
    }

    return {
      label: !roomData.has_stream_commenced && isCommenceable ? 'Join Stream' : 'Stream Has Ended',
      disabled: roomData.has_stream_commenced || !isCommenceable,
      note: '',
    };
  }

  // Admin
  if (isAdmin) {
    if (!isCommenceable) {
      return {
        label: 'Stream not available',
        disabled: true,
        note: 'The stream is not yet ready to be started. Please wait.',
      };
    }

    return {
      label: hasStreamCommenced ? 'Join Stream' : 'Start Stream',
      disabled: false,
    };
  }

  // Participant
  if (now < earliestJoin) {
    return {
      label: 'Waiting for host...',
      disabled: true,
      note: `Stream will be available from ${earliestJoin.toLocaleTimeString()}`,
    };
  }

  if (!isCommenceable) {
    return {
      label: 'Stream not yet live',
      disabled: true,
      note: 'The host has not started the stream yet. Please wait.',
    };
  }

  return {
    label: 'Join Stream',
    disabled: false,
  };
};

export function warnAboutMissingStyles(el?: HTMLElement) {
  if (
    typeof window !== 'undefined' &&
    typeof process !== 'undefined' &&
    // @ts-expect-error This comparison appears to be unintentional because the types '"development" | "production" | "test"' and '"dev"' have no overlap.
    (process?.env?.NODE_ENV === 'dev' || process?.env?.NODE_ENV === 'development')
  ) {
    const target = el ?? document.querySelector('.lk-room-container');
    if (target && !getComputedStyle(target).getPropertyValue('--lk-has-imported-styles')) {
      log.warn(
        "It looks like you're not using the `@livekit/components-styles package`. To render the UI with the default styling, please import it in your layout or page.",
      );
    }
  }
}

export function isProp<U extends HTMLElement, T extends React.HTMLAttributes<U>>(
  prop: T | undefined,
): prop is T {
  return prop !== undefined;
}

interface Props {
  [key: string]: any;
}

type TupleTypes<T> = { [P in keyof T]: T[P] } extends { [key: number]: infer V } ? V : never;
type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends (k: infer I) => void
  ? I
  : never;

/**
 * Calls all functions in the order they were chained with the same arguments.
 */
export function chain(...callbacks: any[]): (...args: any[]) => void {
  return (...args: any[]) => {
    for (const callback of callbacks) {
      if (typeof callback === 'function') {
        try {
          callback(...args);
        } catch (e) {
          console.error(e);
        }
      }
    }
  };
}

/**
 * Merges multiple props objects together. Event handlers are chained,
 * classNames are combined, and ids are deduplicated - different ids
 * will trigger a side-effect and re-render components hooked up with `useId`.
 * For all other props, the last prop object overrides all previous ones.
 * @param args - Multiple sets of props to merge together.
 */
function mergePropsReactAria<T extends Props[]>(...args: T): UnionToIntersection<TupleTypes<T>> {
  // Start with a base clone of the first argument. This is a lot faster than starting
  // with an empty object and adding properties as we go.
  const result: Props = { ...args[0] };
  for (let i = 1; i < args.length; i++) {
    const props = args[i];
    for (const key in props) {
      const a = result[key];
      const b = props[key];

      // Chain events
      if (
        typeof a === 'function' &&
        typeof b === 'function' &&
        // This is a lot faster than a regex.
        key[0] === 'o' &&
        key[1] === 'n' &&
        key.charCodeAt(2) >= /* 'A' */ 65 &&
        key.charCodeAt(2) <= /* 'Z' */ 90
      ) {
        result[key] = chain(a, b);

        // Merge classnames, sometimes classNames are empty string which eval to false, so we just need to do a type check
      } else if (
        (key === 'className' || key === 'UNSAFE_className') &&
        typeof a === 'string' &&
        typeof b === 'string'
      ) {
        result[key] = clsx(a, b);
      } else {
        result[key] = b !== undefined ? b : a;
      }
    }
  }

  return result as UnionToIntersection<TupleTypes<T>>;
}

export function mergeProps<
  U extends HTMLElement,
  T extends Array<React.HTMLAttributes<U> | undefined>,
>(...props: T) {
  return mergePropsReactAria(...props.filter(isProp));
}

export function getRemoteResponsiveImage(
  imageData?: ResponsiveImageData | null,
  breakpoint: Breakpoint = 'small',
) {
  if (!imageData) {
    return '';
  }
  if (breakpoint === 'original') {
    return imageData.url ?? '';
  }
  return imageData.breakpoints?.[breakpoint]?.url ?? imageData?.url ?? '';
}

export const joinStream = async (options: {
  isSocketConnected: boolean;
  socketRef: React.MutableRefObject<Socket | null>;
  livestreamingRoomData: LivestreamingRoomData | null;
  livestreamingSessionData: LivestreamingSessionData | null;
  setIsForcedSetup: React.Dispatch<React.SetStateAction<boolean>>;
  setIsJoiningRoom: React.Dispatch<React.SetStateAction<boolean>>;
  setLocalState: React.Dispatch<React.SetStateAction<GotedoStreamsLocalState>>;
  setLivestreamingSessionData: (value: LivestreamingSessionData | null) => void;
}) => {
  const {
    isSocketConnected,
    socketRef,
    livestreamingRoomData,
    livestreamingSessionData,
    setIsForcedSetup,
    setIsJoiningRoom,
    setLocalState,
    setLivestreamingSessionData,
  } = options;

  if (!isSocketConnected || !socketRef.current || !livestreamingRoomData) {
    return;
  }

  if (livestreamingSessionData && 'room_token' in livestreamingSessionData) {
    setIsForcedSetup(false);
    return;
  }

  setIsJoiningRoom(true);

  try {
    const response = await socketRef.current
      .timeout(5000)
      .emitWithAck('gotedo_streams/join_stream', {
        room_id: livestreamingRoomData.room_id,
        church_id: livestreamingRoomData.church_id,
        meeting_id: livestreamingRoomData.meeting_id,
        join_as_church: !!livestreamingRoomData.joinAsChurch,
      });

    if ('error' in response) {
      if (typeof response.error.message === 'string') {
        toast.error(response.error.message, { duration: 10000 });
      } else if (response.error.errors?.[0]?.message) {
        toast.error(response.error.errors[0].message, { duration: 10000 });
      } else {
        toast.error('There was an unexpected error while joining the stream. Please try again.', {
          duration: 10000,
        });
      }
    } else if ('success' in response && 'data' in response.success) {
      const data: LivestreamingSessionData = response.success.data;
      const hasRoomEnded =
        'can_join' in data && !data.can_join && data.reason === 'stream_not_joinable:has_ended';
      const isTooEarly =
        'can_join' in data && !data.can_join && data.reason === 'stream_not_joinable:too_early';

      setLocalState((prev) => ({
        ...prev,
        roomData: {
          ...prev.roomData!,
          is_too_early_to_stream: isTooEarly,
          has_stream_ended: hasRoomEnded,
        },
      }));

      setIsForcedSetup(false);
      setLivestreamingSessionData(data);
    }
  } catch (error) {
    toast.error('Unexpected error while joining stream.', { duration: 10000 });
    console.error('Join stream error:', error);
  } finally {
    setIsJoiningRoom(false);
  }
};

export function getSourceIcon(source: Track.Source, enabled: boolean, hotMode = false) {
  switch (source) {
    case Track.Source.Microphone:
      return enabled ? <MicIcon hotMode={hotMode} /> : <MicOffIcon />;
    case Track.Source.Camera:
      return enabled ? <VideoIcon /> : <VideoOffIcon />;

    case Track.Source.ScreenShare:
      return enabled ? <ScreenShareStopIcon /> : <ShareScreenIcon />;
    default:
      return undefined;
  }
}

/**
 * Converts a CSS size string (e.g., "6px", "1.5rem", "100%") into a numeric value.
 *
 * @param cssSizeString The CSS size string.
 * @returns The numeric part of the size, or null if conversion fails.
 */
export function convertCssSizeToNumber(cssSizeString: string): number | null {
  if (typeof cssSizeString !== 'string' || cssSizeString.trim() === '') {
    return null;
  }

  // Use a regular expression to match a leading number (integer or float)
  // and capture it. The rest (the unit) is discarded.
  const match = cssSizeString.trim().match(/^-?(\d+(\.\d+)?)/);

  if (match && match[1]) {
    // match[1] contains the captured numeric part (e.g., "6" or "1.5")
    return parseFloat(match[1]);
  }

  // Return null if no numeric part is found (e.g., input is just "auto")
  return null;
}

export function cloneSingleChild(
  children: React.ReactNode | React.ReactNode[],
  props?: Record<string, any>,
  key?: any,
) {
  return React.Children.map(children, (child) => {
    // Checking isValidElement is the safe way and avoids a typescript
    // error too.
    if (React.isValidElement(child) && React.Children.only(children)) {
      if (child.props.className) {
        // make sure we retain classnames of both passed props and child
        props ??= {};
        props.className = clsx(child.props.className, props.className);
        props.style = { ...child.props.style, ...props.style };
      }
      return React.cloneElement(child, { ...props, key });
    }
    return child;
  });
}
